import { useTranslation } from "react-i18next";
import {
  cn,
  Icon,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@nexus/ui";
import type { EventJobItem, EventJobSortBy } from "@/features/events/api/events.api";
import { getRunDurationDisplay } from "../lib/event-processing-display";
import {
  getDurationBarPercent,
  getPublishedDisplay,
  stripTopicPrefix,
  truncateCorrelationId,
} from "../lib/event-row-display";
import { formatPreciseDurationMs, getEventEndToEndMs } from "../lib/event-timing";
import { EventStatusBadge } from "./shared/EventStatusBadge";

interface EventsTableProps {
  rows: EventJobItem[];
  now: number;
  /** Live PROCESSING duration past this shows as timed out. From the ops settings. */
  stuckAfterMs: number;
  /** Run duration that fills the mini bar; p95 keeps the scale stable while paging. */
  durationScaleMs: number | null;
  sortBy: EventJobSortBy;
  sortOrder: "asc" | "desc";
  onSortPublished: () => void;
  onSelectRow: (row: EventJobItem) => void;
  selectedRowId: string | null;
}

const MONO_MICRO_LABEL = "font-mono text-[10px] uppercase tracking-wide text-muted-foreground";

function DurationBar({
  percent,
  status,
}: {
  percent: number | null;
  status: EventJobItem["status"];
}) {
  const fillClass =
    status === "FAILED" ? "bg-error" : status === "PROCESSING" ? "bg-info" : "bg-chart-spend";

  return (
    <span
      aria-hidden
      className="block h-[3px] w-14 shrink-0 overflow-hidden rounded-full bg-border-subtle"
    >
      {percent == null ? null : (
        <span
          className={cn("block h-full rounded-full", fillClass)}
          style={{ width: `${percent}%` }}
        />
      )}
    </span>
  );
}

export function EventsTable({
  rows,
  now,
  stuckAfterMs,
  durationScaleMs,
  sortBy,
  sortOrder,
  onSortPublished,
  onSelectRow,
  selectedRowId,
}: EventsTableProps) {
  const { t, i18n } = useTranslation();
  const isSortedByPublished = sortBy === "publishedAt";

  return (
    <Table className="table-fixed">
      <colgroup>
        <col className="w-[110px]" />
        <col className="w-[112px]" />
        <col />
        <col className="w-[170px]" />
        <col className="w-[150px]" />
        <col className="w-[130px]" />
        <col className="w-[100px]" />
        <col className="w-9" />
      </colgroup>
      <TableHeader className="sticky top-0 z-10">
        <TableRow className="hover:bg-muted">
          <TableHead
            aria-sort={
              isSortedByPublished
                ? sortOrder === "asc"
                  ? "ascending"
                  : "descending"
                : "none"
            }
            className="p-0"
          >
            <button
              type="button"
              onClick={onSortPublished}
              className="flex h-9 w-full items-center gap-1 px-4 text-left uppercase"
            >
              {t("admin.events.table.published", "Published")}
              <Icon
                name="chevron-down"
                size="xs"
                aria-hidden
                className={cn(
                  "transition-transform",
                  isSortedByPublished && sortOrder === "asc" && "rotate-180",
                  !isSortedByPublished && "opacity-40",
                )}
              />
            </button>
          </TableHead>
          <TableHead>{t("admin.events.table.status", "Status")}</TableHead>
          <TableHead>{t("admin.events.table.event", "Event")}</TableHead>
          <TableHead>{t("admin.events.table.correlation", "Correlation")}</TableHead>
          <TableHead>{t("admin.events.table.user", "User")}</TableHead>
          <TableHead>{t("admin.events.table.runDuration", "Run duration")}</TableHead>
          <TableHead>{t("admin.events.table.endToEnd", "End to end")}</TableHead>
          <TableHead />
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => {
          const published = getPublishedDisplay(row.publishedAt, i18n.language);
          const runDuration = getRunDurationDisplay(row, now, stuckAfterMs);
          const endToEndMs = getEventEndToEndMs(row);
          const isFailed = row.status === "FAILED";

          return (
            <TableRow
              key={row.id}
              onClick={() => onSelectRow(row)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  onSelectRow(row);
                }
              }}
              tabIndex={0}
              aria-label={t("admin.events.table.rowLabel", "Open event {{eventType}}", {
                eventType: row.eventType,
              })}
              data-state={selectedRowId === row.id ? "selected" : undefined}
              className={cn(
                "cursor-pointer focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring",
                isFailed && "bg-error-subtle",
              )}
            >
              <TableCell className="whitespace-nowrap">
                <div className="tabular-nums">{published.time}</div>
                <div className="text-[11px] text-muted-foreground">{published.date}</div>
              </TableCell>
              <TableCell>
                <EventStatusBadge status={row.status} />
              </TableCell>
              <TableCell className="min-w-0">
                <div className="truncate" title={row.eventType}>
                  {row.eventType}
                </div>
                <div className={cn(MONO_MICRO_LABEL, "truncate")}>
                  {stripTopicPrefix(row.topic)}
                </div>
              </TableCell>
              <TableCell
                className="whitespace-nowrap font-mono text-[11px] text-muted-foreground"
                title={row.correlationId}
              >
                {truncateCorrelationId(row.correlationId)}
              </TableCell>
              <TableCell className="truncate whitespace-nowrap">
                {row.actorUserName ?? row.actorUserId ?? "—"}
              </TableCell>
              <TableCell className="whitespace-nowrap">
                <div className="flex items-center gap-2">
                  <span
                    className={cn(
                      "w-[34px] tabular-nums",
                      isFailed && "text-error",
                      row.status === "PROCESSING" && "text-info",
                      runDuration.timedOut && "text-warning",
                    )}
                    title={runDuration.timedOut ? runDuration.durationText : undefined}
                  >
                    {runDuration.durationText}
                  </span>
                  <DurationBar
                    percent={getDurationBarPercent(row.runDurationMs, durationScaleMs)}
                    status={row.status}
                  />
                </div>
              </TableCell>
              <TableCell
                className="whitespace-nowrap tabular-nums"
                title={t(
                  "admin.events.table.endToEndHint",
                  "From the click, so time spent queued counts",
                )}
              >
                {endToEndMs == null ? (
                  <span className="text-muted-foreground">—</span>
                ) : (
                  formatPreciseDurationMs(endToEndMs)
                )}
              </TableCell>
              <TableCell className="px-2 text-muted-foreground">
                <Icon name="chevron-right" size="xs" aria-hidden />
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
